/**
 * 416: what the commander's skills (src/ai/skills/) read alike — a spot the ground or the props hide a squad at from the
 * eyes it expects fire from (the ambush's site, the rally's point), and what took a squad from a skill (its ledger's `by`).
 * Pure.
 */
import type { GameState, Stance, Vec3 } from '../../types';
import { personSetOf, PERSON_BODY_HEIGHT } from '../../core/scaleProfile';
import { propsCoverBody, stanceSphereRadius, terrainSpotsNear } from '../../core/coverMap';
import { stanceCentreHeight } from '../../core/stance';
import { personPointOn } from '../../core/fineTerrain';
import { squadOf, taskRank } from '../../core/squads';
import { vecCross, vecDot, vecNormalize, vecScale, vecAdd } from '../../math';
import { dacos, dsin } from '../../core/dmath';

export const STANCE_RANK = { prone: 0, crouch: 1, stand: 2 } as const;
/** The angle between two directions (radians). */
export const arc = (a: Vec3, b: Vec3): number => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b)))));
/** The direction `t` of the way from `a` to `b` along the great circle. */
export const slerp = (a: Vec3, b: Vec3, t: number): Vec3 => {
  const w = arc(a, b);
  if (w < 1e-9) return vecNormalize(a);
  const s = dsin(w);
  return vecNormalize(vecAdd(vecScale(vecNormalize(a), dsin((1 - t) * w) / s), vecScale(vecNormalize(b), dsin(t * w) / s)));
};
/** The two directions `side` (radians) to either side of `on`, across its great circle with `along` (along the ground). */
export function besides(on: Vec3, along: Vec3, side: number): [Vec3, Vec3] {
  const across = vecNormalize(vecCross(vecNormalize(on), vecNormalize(along)));
  return [slerp(on, vecAdd(vecNormalize(on), vecScale(across, side)), 1), slerp(on, vecAdd(vecNormalize(on), vecScale(across, -side)), 1)];
}

/** How a hidden spot is read: the stance it hides, how near `here` it lies, and the room round it for the squad (people). */
export interface HideDials { hideStance: Stance; spotPeople: number; roomPeople: number; roomSpots: number }

/**
 * The spot nearest `here` (within `spotPeople`) where a body at `hideStance` is hidden from every one of `eyes` (world
 * points) — by the ground (it hides that stance or taller, and a taller stance's eye sees the eye: risen, it fires), or by
 * the props (70.3) where the ground does not — with `roomSpots` such spots (it among them) within `roomPeople` of it. Spots
 * stand on the relief lattice, so the same spot is the same direction for every eye. Null when none.
 */
export function hiddenSpot(state: GameState, planetIndex: number, here: Vec3, eyes: readonly Vec3[], d: HideDials): Vec3 | null {
  const planet = state.planets[planetIndex];
  if (!planet || eyes.length === 0) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, r = planet.radius;
  const hideUp = stanceCentreHeight(d.hideStance, ps), hideR = stanceSphereRadius(d.hideStance, ps);
  const key = (x: Vec3) => `${x.x},${x.y},${x.z}`;
  const fitFrom = (eye: Vec3): Map<string, Vec3> => {
    const out = new Map<string, Vec3>();
    for (const s of terrainSpotsNear(state, planetIndex, here, (d.spotPeople + d.roomPeople) * person, eye)) {
      if (s.sees === null || s.sees === 'prone') continue;
      const byGround = s.hides !== null && STANCE_RANK[s.hides] >= STANCE_RANK[d.hideStance] && STANCE_RANK[s.sees] > STANCE_RANK[s.hides];
      if (!byGround && !((s.hides === null || STANCE_RANK[s.hides] < STANCE_RANK[d.hideStance]) ? propsCoverBody(state, eye, personPointOn(planet, s.dir, ps, hideUp), vecNormalize(s.dir), hideR) : false)) continue;
      out.set(key(s.dir), s.dir);
    }
    return out;
  };
  let fits = fitFrom(eyes[0]);
  for (const e of eyes.slice(1)) { if (fits.size === 0) break; const o = fitFrom(e); fits = new Map([...fits].filter(([k]) => o.has(k))); }
  const room = d.roomPeople * person, reach = d.spotPeople * person;
  let pick: Vec3 | null = null, near = Infinity;
  for (const x of fits.values()) {
    const dd = arc(x, here) * r;
    if (dd > reach || dd >= near) continue;
    let n = 0;
    for (const o of fits.values()) if (arc(o, x) * r <= room) n++;
    if (n >= d.roomSpots) { near = dd; pick = x; }
  }
  return pick;
}

/**
 * What took a squad from a skill (`claimed`): its best standing task above the routine's rank — a human's order, a lift,
 * a member's errand (by its order's kind), a play, an objective's claim — else `planner` (an order of this step's own, not
 * yet in the state). For the ledgers: which planner outranks the skill, before any rank is moved.
 */
export function takenBy(st: GameState, unitIds: readonly number[], tick: number): string {
  let best: { r: number; name: string } | null = null;
  for (const t of squadOf(st, unitIds[0])?.tasks ?? []) {
    if (t.order.kind === 'mark' || (t.expires !== undefined && tick >= t.expires)) continue;
    const r = taskRank(t);
    if (r >= 6 || (best && best.r <= r)) continue;
    const name = t.by === 'human' ? 'human' : t.claim ?? (t.units ? `errand:${t.order.kind}` : t.play !== undefined ? `play:${t.order.kind}` : `task:${t.order.kind}`);
    best = { r, name };
  }
  return best?.name ?? 'planner';
}
